/**
 * Wholesale quick-order custom element.
 *
 * Handles per-row "Add to cart": posts to /cart/add.js, then dispatches a
 * `cart:update` event (the theme's CartAddEvent name) so the header cart icon
 * refreshes and the cart drawer auto-opens (when `auto_open_cart_drawer` is on).
 * As a belt-and-suspenders, also calls cart-drawer-component.open() directly.
 *
 * No build step / no bundler imports — this file is loaded via <script> tag
 * directly from the theme's assets and runs in plain browser context.
 */
(function () {
  var CART_UPDATE_EVENT = 'cart:update';
  var CART_ADD_URL = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

  function gatherCartSectionIds() {
    var ids = [];
    var nodes = document.querySelectorAll('cart-items-component[data-section-id]');
    for (var i = 0; i < nodes.length; i++) {
      var id = nodes[i].getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    }
    var drawer = document.querySelector('cart-drawer-component');
    if (drawer) {
      var dSection = drawer.closest('[data-section-id]');
      if (dSection) {
        var did = dSection.getAttribute('data-section-id');
        if (did && ids.indexOf(did) === -1) ids.push(did);
      }
    }
    return ids;
  }

  function dispatchCartUpdate(cart, sourceId, data) {
    var event = new Event(CART_UPDATE_EVENT, { bubbles: true });
    event.detail = {
      resource: cart,
      sourceId: sourceId,
      data: data || {},
    };
    document.dispatchEvent(event);
  }

  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;
    if (typeof drawer.open === 'function') {
      try {
        drawer.open();
      } catch (err) {
        /* ignore — drawer will still open via auto-open if enabled */
      }
    }
  }

  var WholesaleQuickOrder = /** @class */ (function () {
    function WholesaleQuickOrder(root) {
      this.root = root;
      this.handleClick = this.handleClick.bind(this);
      this.handleQtyStep = this.handleQtyStep.bind(this);
      this.root.addEventListener('click', this.handleClick);
      this.root.addEventListener('click', this.handleQtyStep);
    }

    WholesaleQuickOrder.prototype.handleQtyStep = function (event) {
      var stepButton = event.target.closest('[data-wholesale-qty-step]');
      if (!stepButton || !this.root.contains(stepButton)) return;
      var variantId = stepButton.getAttribute('data-variant-id');
      var step = parseInt(stepButton.getAttribute('data-wholesale-qty-step') || '1', 10);
      var input = this.root.querySelector(
        '[data-wholesale-qty][data-variant-id="' + variantId + '"]'
      );
      if (!input) return;
      var current = parseInt(input.value, 10) || 1;
      var next = Math.max(1, current + step);
      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    WholesaleQuickOrder.prototype.handleClick = function (event) {
      var button = event.target.closest('[data-wholesale-add-to-cart]');
      if (!button || !this.root.contains(button)) return;
      event.preventDefault();
      this.addToCart(button);
    };

    WholesaleQuickOrder.prototype.addToCart = function (button) {
      var self = this;
      if (button.getAttribute('aria-busy') === 'true') return;

      var variantId = button.getAttribute('data-variant-id');
      var productId = button.getAttribute('data-product-id');
      if (!variantId) return;

      var qtyInput = this.root.querySelector(
        '[data-wholesale-qty][data-variant-id="' + variantId + '"]'
      );
      var quantity = Math.max(1, parseInt((qtyInput && qtyInput.value) || '1', 10));

      button.setAttribute('aria-busy', 'true');
      button.disabled = true;

      var sectionIds = gatherCartSectionIds();
      var payload = { items: [{ id: Number(variantId), quantity: quantity }] };
      if (sectionIds.length) payload.sections = sectionIds.join(',');

      fetch(CART_ADD_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      })
        .then(function (response) {
          return response.json().then(function (data) {
            return { ok: response.ok, data: data };
          });
        })
        .then(function (result) {
          var data = result.data || {};
          if (!result.ok || data.status) {
            self.showError(button, data.description || data.message || 'Could not add to cart');
            return;
          }

          return fetch('/cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              dispatchCartUpdate(cart, 'wholesale-quick-order', {
                source: 'wholesale-quick-order',
                itemCount: cart && cart.item_count,
                variantId: String(variantId),
                productId: productId,
                sections: data.sections,
              });
              openCartDrawer();
            });
        })
        .catch(function (err) {
          console.error('[wholesale-quick-order] add failed', err);
          self.showError(button, 'Network error, please try again.');
        })
        .then(function () {
          button.setAttribute('aria-busy', 'false');
          button.disabled = false;
        });
    };

    WholesaleQuickOrder.prototype.showError = function (button, message) {
      var cell = button.closest('.app-wholesale-quick-order__cell--action');
      if (!cell) return;
      var el = cell.querySelector('.app-wholesale-quick-order__error');
      if (!el) {
        el = document.createElement('span');
        el.className = 'app-wholesale-quick-order__error';
        el.setAttribute('role', 'alert');
        cell.appendChild(el);
      }
      el.textContent = message;
      window.setTimeout(function () {
        if (el && el.parentNode) el.textContent = '';
      }, 4000);
    };

    return WholesaleQuickOrder;
  })();

  if (!customElements.get('wholesale-quick-order')) {
    customElements.define(
      'wholesale-quick-order',
      /** @class */ (function (HTMLElementCtor) {
        function WholesaleQuickOrderElement() {
          var instance = Reflect.construct(HTMLElementCtor, [], WholesaleQuickOrderElement);
          return instance;
        }
        WholesaleQuickOrderElement.prototype = Object.create(HTMLElementCtor.prototype);
        WholesaleQuickOrderElement.prototype.constructor = WholesaleQuickOrderElement;
        WholesaleQuickOrderElement.prototype.connectedCallback = function () {
          if (this._initialized) return;
          this._initialized = true;
          new WholesaleQuickOrder(this);
        };
        return WholesaleQuickOrderElement;
      })(HTMLElement)
    );
  }
})();
